import { useState } from "react";
import {
  Modal,
  Stack,
  Table,
  Checkbox,
  Button,
  Group,
  Text,
  Loader,
  Alert,
  Badge,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconBulb, IconInfoCircle } from "@tabler/icons-react";
import { suggestPrompts, batchCreatePrompts } from "./promptsApi";
import { usePromptsStore } from "./promptsStore";
import QueryTypeBadge from "../_Shared/QueryTypeBadge";

export default function SuggestPromptsModal({ opened, onClose, brandId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const { fetchPrompts } = usePromptsStore();

  const handleOpen = async () => {
    if (hasFetched) return;
    setLoading(true);
    setError(null);
    try {
      const result = await suggestPrompts(brandId);
      setSuggestions(result);
      setSelected(new Set(result.map((_, i) => i)));
      setHasFetched(true);
    } catch (err) {
      setError(err.message || "Failed to generate suggestions");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuggestions([]);
    setSelected(new Set());
    setHasFetched(false);
    setError(null);
    onClose();
  };

  const toggleSelect = (index) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === suggestions.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(suggestions.map((_, i) => i)));
    }
  };

  const handleAdd = async () => {
    const toAdd = suggestions
      .filter((_, i) => selected.has(i))
      .map((s) => ({
        brandId: s.brandId,
        prompt: s.prompt,
        queryType: s.queryType,
        industry: s.industry,
        aspect: s.aspect,
        defaultTargetCountryCode: s.defaultTargetCountryCode,
        defaultTargetLanguageCode: s.defaultTargetLanguageCode,
        status: "auto",
      }));

    if (toAdd.length === 0) return;

    setSubmitting(true);
    try {
      await batchCreatePrompts(brandId, toAdd);
      notifications.show({
        title: "Prompts added",
        message:
          `Added ${toAdd.length} suggested prompts. ` +
          "Any past the plan watch-list were saved off.",
        color: "green",
      });
      await fetchPrompts(brandId);
      handleClose();
    } catch (err) {
      notifications.show({
        title: "Failed to add prompts",
        message: err.message,
        color: "red",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconBulb size={20} />
          <Text fw={600}>AI-Suggested Prompts</Text>
        </Group>
      }
      size="xl"
      onOpen={handleOpen}
    >
      <Stack>
        {!hasFetched && !loading && !error && (
          <Stack align="center" gap="md" py="lg">
            <Text size="sm" c="dimmed" ta="center">
              Generate AI-powered prompt suggestions based on your brand's
              profile, industries, and topics.
            </Text>
            <Button
              leftSection={<IconBulb size={16} />}
              onClick={handleOpen}
              loading={loading}
            >
              Generate Suggestions
            </Button>
          </Stack>
        )}

        {loading && (
          <Stack align="center" py="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Analyzing your brand and generating suggestions...
            </Text>
          </Stack>
        )}

        {error && (
          <Alert icon={<IconInfoCircle />} color="red" title="Error">
            {error}
          </Alert>
        )}

        {hasFetched && suggestions.length === 0 && (
          <Text size="sm" c="dimmed" ta="center" py="lg">
            No additional suggestions found. Your brand already has good prompt
            coverage.
          </Text>
        )}

        {suggestions.length > 0 && (
          <>
            <Text size="sm" c="dimmed">
              Select the prompts you'd like to add to your monitoring list.
            </Text>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40}>
                    <Checkbox
                      checked={selected.size === suggestions.length}
                      indeterminate={
                        selected.size > 0 &&
                        selected.size < suggestions.length
                      }
                      onChange={toggleAll}
                    />
                  </Table.Th>
                  <Table.Th>Prompt</Table.Th>
                  <Table.Th w={120}>Type</Table.Th>
                  <Table.Th w={140}>Category</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {suggestions.map((s, i) => (
                  <Table.Tr
                    key={i}
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleSelect(i)}
                  >
                    <Table.Td>
                      <Checkbox
                        checked={selected.has(i)}
                        onChange={() => toggleSelect(i)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{s.prompt}</Text>
                    </Table.Td>
                    <Table.Td>
                      <QueryTypeBadge queryType={s.queryType} />
                    </Table.Td>
                    <Table.Td>
                      {s.industry && (
                        <Badge variant="light" size="sm">
                          {s.industry}
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {selected.size} of {suggestions.length} selected
              </Text>
              <Group>
                <Button variant="default" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAdd}
                  loading={submitting}
                  disabled={selected.size === 0}
                >
                  Add {selected.size} Prompt{selected.size !== 1 ? "s" : ""}
                </Button>
              </Group>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
